// The office "assign an engineer" field for a training session (Session AGG). Shared by the create
// and edit modals so both offer the same options, resolved from the same endpoint.
//
// Assigning is OPTIONAL and the empty option is first: an unassigned session is visible to every
// engineer, which is the sensible default and means the office never has to assign one for it to be
// deliverable.
//
// The helper text says what assigning COSTS, because it is not obvious and it is the sharp end of
// this control: assigning narrows the session to one phone and takes it off everyone else's. A wrong
// pick is therefore worse than no pick, since the engineer actually going stops seeing it.
//
// A native select rather than a search box: ELE has a handful of engineers, a native select is
// already type-to-search with a keyboard, and it needs no dropdown of its own on a phone. It stays
// usable well past the point where this list could realistically grow.
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import type { TrainingAssignableEngineer } from "../../lib/trainingTypes";

export function AssignEngineerField({
  value,
  onChange,
  id = "training-assigned-engineer",
  labelClassName,
  selectClassName,
  disabled = false,
}: {
  // A USER id, or "" for unassigned.
  value: string;
  /**
   * The second argument is the chosen engineer's NAME (null when unassigned, or when the id is one
   * the loaded list does not contain). Session AGM added it so a caller can show WHO was picked
   * without fetching the list a second time; callers that only need the id can ignore it.
   */
  onChange: (userId: string, engineerName: string | null) => void;
  id?: string;
  labelClassName?: string;
  selectClassName?: string;
  /**
   * Held while a save is in flight (Session AGN, for the detail page, which saves on change rather
   * than on a Save button). Without it two quick changes race and the field can settle showing the
   * earlier of the two. The modals never pass it: they save on submit, not on change.
   */
  disabled?: boolean;
}) {
  const [engineers, setEngineers] = useState<TrainingAssignableEngineer[]>([]);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    api.training
      .engineers()
      .then((rows) => {
        if (live) setEngineers(rows);
      })
      .catch(() => {
        if (live) setFailed(true);
      });
    return () => {
      live = false;
    };
  }, []);

  // A value already on the session whose engineer is no longer in the options (deactivated, or the
  // list failed to load) must still be shown as chosen, so opening the form cannot silently clear it.
  const known = engineers.some((e) => e.id === value);

  return (
    <div>
      <label className={labelClassName} htmlFor={id}>
        Assign to an engineer (optional)
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value, engineers.find((x) => x.id === e.target.value)?.name ?? null)}
        className={selectClassName}
        disabled={disabled}
      >
        <option value="">Any engineer</option>
        {value && !known && <option value={value}>Currently assigned engineer</option>}
        {engineers.map((e) => (
          <option key={e.id} value={e.id}>
            {e.name}
          </option>
        ))}
      </select>
      <p className="mt-1 text-xs text-ele-body">
        {failed
          ? "The engineer list could not be loaded. Leave this unassigned and set it later."
          : "Name the engineer who will deliver it. Left unassigned, it shows on no engineer's phone."}
      </p>
    </div>
  );
}
